import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { UserDisplayComponent } from '@shared/components/user-display/user-display.component';
import { switchMap } from 'rxjs';
import { PricingCountryService } from '../../../services/pricing-country.service';
import { PricingCountryState } from '../../../store/pricing-country.state';
import {
  CountryBundleRow,
  CountryPriceType,
  PricingCountry,
} from '../../../types/pricing.types';

/** "All" shows both types' history interleaved (API: no `type` param). */
const ALL_TYPES = 'ALL';
type HistoryFilter = CountryPriceType | typeof ALL_TYPES;

@Component({
  selector: 'app-country-price-history-dialog',
  imports: [
    DecimalPipe,
    DataTableComponent,
    TranslatePipe,
    UserDisplayComponent,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: './country-price-history-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountryPriceHistoryDialogComponent implements OnInit {
  public countryId = signal<string>(null);
  public row = signal<CountryBundleRow>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private pricingCountryService = inject(PricingCountryService);

  protected pricingCountry: Signal<PricingCountry> = computed(() =>
    this.store.selectSignal(PricingCountryState.getPricingCountryById)()(
      this.countryId()
    )
  );

  protected typeFilter = signal<HistoryFilter>(ALL_TYPES);
  protected readonly typeFilters: HistoryFilter[] = [
    ALL_TYPES,
    CountryPriceType.WSP,
    CountryPriceType.EUP,
  ];

  protected dataTableConfiguration: DataTableConfiguration;

  private priceColumnTemplate = viewChild<TemplateRef<any>>(
    'priceColumnTemplate'
  );
  private generatedByColumnTemplate = viewChild<TemplateRef<any>>(
    'generatedByColumnTemplate'
  );

  private typeFilter$ = toObservable(this.typeFilter);

  ngOnInit(): void {
    this.dataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [],
      columns: [
        {
          label: 'pricing.country.details.columns.type',
          property: 'type',
        },
        {
          label: 'pricing.country.details.columns.price',
          propertyTemplate: this.priceColumnTemplate(),
        },
        {
          label: 'pricing.country.details.columns.validFrom',
          property: 'validFrom',
          type: DataTableDataType.DATE,
        },
        {
          label: 'pricing.country.details.columns.generatedBy',
          propertyTemplate: this.generatedByColumnTemplate(),
        },
      ],
      rows$: this.typeFilter$.pipe(
        switchMap((typeFilter) =>
          this.pricingCountryService.getPricingCountryBundlePriceHistory(
            this.countryId(),
            this.row().id,
            typeFilter === ALL_TYPES ? undefined : typeFilter
          )
        )
      ),
    };
  }
}
